import { BehaviorSubject, distinctUntilChanged, interval, of, shareReplay, Subject, switchMap, withLatestFrom, } from 'rxjs';
import { contextValues } from '@streem/logger';
import { invariant } from '@streem/toolbox';
import { streemLogger } from './util/streem_logger';
import { ChimeLogger } from './util/chime_logger';
import { getPermissionsState } from './util/permission_state';
import { ConnectionHealthPolicyConfiguration, DefaultDeviceController, DefaultMeetingSession, MeetingSessionConfiguration, ServerSideNetworkAdaption, VideoPriorityBasedPolicy, VideoPriorityBasedPolicyConfig, } from 'amazon-chime-sdk-js';
import { ChimeLocalDataTrack } from './tracks/data_tracks';
import { ChimeLocalVideoTrack } from './tracks/video_tracks';
import { ChimeLocalAudioTrack, ChimeRemoteAudioTrack } from './tracks/audio_tracks';
import { ChimeDeviceObserver } from './observers/chime_device_observer';
import { ChimeMeetingObserver } from './observers/chime_meeting_observer';
import { ChimeConnectionObserver } from './observers/chime_connection_observer';
import { createVolumeIndicatorCallback } from './observers/volume_indicator_callback';
import { LowBandwidthManager } from './managers/low_bandwidth_manager';
import { RoomMonitor } from './room_monitor';
import { NetworkQualityObserver } from './observers/network_quality_observer';
import { RemoteAttendeeManager } from './managers/remote_attendee_manager';
import { RemoteAttendeeObserver } from './observers/remote_attendee_observer';
import { DownlinkObserver } from './observers/video_downlink_observer';
import { VideoPriorityBasedPolicyManager } from './managers/video_priority_based_policy_manager';
import { VIDEO_HEIGHT, VIDEO_WIDTH } from './constants';
export class ChimeRoom {
    constructor({ roomVideoCredentials, remoteParticipantCapabilities, isHostSession, streemRoomId, chimeLowBandwidthOptimizationEnabled, preferredVideoCodecs, isVideoProviderVerboseLoggingEnabled = false, chimeNetworkAdaptationEnabled = false, }) {
        this.streemLogger = streemLogger.extend('sdk-chime-video:ChimeRoom');
        this.connectionStateSubject = new BehaviorSubject('DISCONNECTED');
        this.connectionState = this.connectionStateSubject.asObservable();
        // error subjects
        this.getUserMediaVideoErrorSubject = new BehaviorSubject(undefined);
        this.getUserMediaVideoError = this.getUserMediaVideoErrorSubject.asObservable();
        this.getUserMediaAudioErrorSubject = new BehaviorSubject(undefined);
        this.getUserMediaAudioError = this.getUserMediaAudioErrorSubject.asObservable();
        // local audio
        this.localAudioTrackSubject = new BehaviorSubject(undefined);
        this.localAudioTrack = this.localAudioTrackSubject.asObservable();
        this.localAudioLevelSubject = new BehaviorSubject(0);
        this.isLocalAudioTrackEnabled = this.buildIsAudioTrackEnabledObservable(this.localAudioTrack);
        // local data
        this.localDataTrackSubject = new BehaviorSubject(undefined);
        this.localDataTrack = this.localDataTrackSubject.asObservable();
        this.localDevicesSubject = new BehaviorSubject([]);
        this.localDevices = this.localDevicesSubject.asObservable();
        // local video
        this.localVideoTrackSubject = new BehaviorSubject(undefined);
        this.localVideoTrack = this.localVideoTrackSubject.asObservable();
        this.isLocalVideoTrackEnabled = this.buildIsVideoTrackEnabledObservable(this.localVideoTrack);
        // remote audio
        this.remoteAudioTrackSubject = new BehaviorSubject(undefined);
        this.remoteAudioTrack = this.remoteAudioTrackSubject.asObservable();
        this.remoteConnectionSubject = new BehaviorSubject(false);
        this.remoteConnection = this.remoteConnectionSubject.asObservable();
        this.remoteAudioLevelSubject = new BehaviorSubject(0);
        // start at 1 to prevent unnecessarily showing the network quality indicator message at the beginning of the call
        this.remoteSignalStrengthSubject = new BehaviorSubject(1);
        this.remoteSignalStrength = this.remoteSignalStrengthSubject.pipe(distinctUntilChanged());
        // remote data
        this.remoteDataTrackSubject = new BehaviorSubject(undefined);
        this.remoteDataTrack = this.remoteDataTrackSubject.asObservable();
        // remote video
        this.remoteVideoTrackSubject = new BehaviorSubject(undefined);
        this.remoteVideoTrack = this.remoteVideoTrackSubject.asObservable();
        this.remoteVideoAvailableSubject = new BehaviorSubject(false);
        // Note: The remote track is not reliably set to disabled by Chime when the remote user's camera
        //       is turned off, so videoAvailable is used.  videoAvailable is true when any remote user's
        //       video is available, which works here since there is only a single remote user.
        this.isRemoteVideoTrackEnabled = this.remoteVideoAvailableSubject.asObservable();
        this.isRemoteVideoTrackBackgrounded = this.buildIsVideoTrackBackgroundedObservable(this.remoteVideoTrack);
        // recording
        this.recordingSubject = new BehaviorSubject(false);
        this.recording = this.recordingSubject.asObservable();
        // room monitor
        // Note: start at a number other than 0 to prevent unnecessarily showing the network quality indicator
        //       message at the beginning of the call
        this.localNetworkQualitySubject = new BehaviorSubject(3);
        this.localNetworkQuality = this.localNetworkQualitySubject.asObservable();
        this.remoteNetworkQualitySubject = new BehaviorSubject(3);
        this.remoteNetworkQuality = this.remoteNetworkQualitySubject.asObservable();
        this.isLocalVideoFrozenSubject = new BehaviorSubject(false);
        this.isLocalVideoFrozen = this.isLocalVideoFrozenSubject.asObservable();
        this.trackProviderName = 'Chime';
        this.useNamedVideoTracks = false;
        this.canReceiveVideo = false;
        this.localAttendeeManagedTracks = [];
        this.mediaWebRtcStatsSubject = new Subject();
        this.mediaWebRtcStats = this.mediaWebRtcStatsSubject.asObservable();
        // null is the default value for the videoPriorityBasedPolicy:
        //    https://aws.github.io/amazon-chime-sdk-js/classes/meetingsessionconfiguration.html#videodownlinkbandwidthpolicy
        this.videoPriorityBasedPolicy = null;
        this.onLocalNetworkQualityChange = (quality) => {
            this.localNetworkQualitySubject.next(quality);
        };
        this.onLocalAudioLevelUpdate = (audioLevel) => {
            this.localAudioLevelSubject.next(audioLevel);
        };
        this.onRemoteNetworkQualityChange = (quality) => {
            this.remoteNetworkQualitySubject.next(quality);
        };
        this.onReconnecting = () => {
            this.streemLogger.info('Reconnecting to room');
            this.connectionStateSubject.next('RECONNECTING');
        };
        this.onReconnected = () => {
            this.streemLogger.info('Reconnected to room');
            this.connectionStateSubject.next('CONNECTED');
        };
        this.onRemoteSignalStrengthUpdate = (signalStrength) => {
            this.remoteSignalStrengthSubject.next(signalStrength);
        };
        this.onRemoteAudioLevelUpdate = (audioLevel) => {
            this.remoteAudioLevelSubject.next(audioLevel);
        };
        this.onRemoteAttendeeAdded = ({ dataTrack, videoTrack }) => {
            this.remoteDataTrackSubject.next(dataTrack);
            this.remoteVideoTrackSubject.next(videoTrack);
            this.remoteConnectionSubject.next(true);
        };
        this.onRemoteAttendeeRemoved = () => {
            this.remoteDataTrackSubject.next(undefined);
            this.remoteVideoTrackSubject.next(undefined);
            this.remoteConnectionSubject.next(false);
        };
        this.onRemoteVideoAvailableChange = (available) => {
            this.remoteVideoAvailableSubject.next(available);
        };
        // Note: references to Attendee.externalUserId is set to the Streem userId in the Backend.
        this.streemRoomId = streemRoomId;
        this.roomVideoCredentials = roomVideoCredentials;
        this.streemLogger.setContextValue(contextValues.ROOM_ID, streemRoomId.toString());
        this.streemLogger.setContextValue(contextValues.CHIME_ROOM_ID, roomVideoCredentials.Meeting.MeetingId);
        this.chimeLogger = new ChimeLogger(this.streemLogger, isVideoProviderVerboseLoggingEnabled);
        this.deviceController = new DefaultDeviceController(this.chimeLogger);
        this.remoteCapabilitiesListener = remoteParticipantCapabilities.subscribe(capabilities => {
            this.canReceiveVideo = capabilities?.canReceiveVideo ?? true;
        });
        this.isHostSession = isHostSession;
        this.lowBandwidthOptimizationEnabled = chimeLowBandwidthOptimizationEnabled;
        this.preferredVideoCodecs = preferredVideoCodecs;
        this.streemLogger.info('ChimeRoom created');
        this.chimeNetworkAdaptationEnabled = chimeNetworkAdaptationEnabled;
    }
    initializeSession() {
        const { Meeting, Attendee } = this.roomVideoCredentials;
        const configuration = new MeetingSessionConfiguration(Meeting, Attendee);
        const healthPolicyConfiguration = new ConnectionHealthPolicyConfiguration();
        //
        // connectionUnhealthyThreshold corresponds to the max value of consecutiveStatsWithNoPackets in
        //     ConnectionHealthData (see: https://aws.github.io/amazon-chime-sdk-js/interfaces/audiovideoobserver.html#connectionhealthdidchange)
        //     Based on observation, this value is incremented every second when there is no network connection.
        //     This means that connectionUnhealthyThreshold = X will wait roughly X seconds before
        //     reconnecting.  This corresponds (approximately) to the time it takes for
        //     the wall sync connection to reconnect.
        healthPolicyConfiguration.connectionUnhealthyThreshold = 2;
        configuration.connectionHealthPolicyConfiguration = healthPolicyConfiguration;
        if (this.chimeNetworkAdaptationEnabled) {
            this.streemLogger.info('Enabling Network adaptation');
            // enable server side network adaption:
            //   https://aws.github.io/amazon-chime-sdk-js/modules/prioritybased_downlink_policy.html#enabling-server-side-network-adaption-features-
            const videoPriorityPolicyConfig = VideoPriorityBasedPolicyConfig.Default;
            videoPriorityPolicyConfig.serverSideNetworkAdaption =
                ServerSideNetworkAdaption.BandwidthProbingAndRemoteVideoQualityAdaption;
            this.videoPriorityBasedPolicy = new VideoPriorityBasedPolicy(this.chimeLogger, videoPriorityPolicyConfig);
            this.videoDownlinkObserver = new DownlinkObserver();
            this.videoPriorityBasedPolicy.addObserver(this.videoDownlinkObserver);
            configuration.videoDownlinkBandwidthPolicy = this.videoPriorityBasedPolicy;
        }
        else {
            this.streemLogger.debug('Network adaptation not enabled', {
                userSid: Attendee.ExternalUserId,
                meetingId: Meeting.MeetingId,
            });
        }
        this.meetingSession = new DefaultMeetingSession(configuration, this.chimeLogger, this.deviceController);
    }
    async registerDeviceManager(meetingSession) {
        const audioInputDevices = await this.deviceController.listAudioInputDevices();
        const audioOutputDevices = await this.deviceController.listAudioOutputDevices();
        const videoInputDevices = await this.deviceController.listVideoInputDevices();
        this.deviceManager = new ChimeDeviceObserver(devices => {
            this.streemLogger.setContextValue(contextValues.AVAILABLE_MEDIA_DEVICES, devices);
            this.streemLogger.info('Available media devices updated', {
                audioInputDevices: devices.filter(device => device.kind === 'audioinput'),
                audioOutputDevices: devices.filter(device => device.kind === 'audiooutput'),
                videoInputDevices: devices.filter(device => device.kind === 'videoinput'),
            });
            this.streemLogger.removeContextValue(contextValues.AVAILABLE_MEDIA_DEVICES);
            this.localDevicesSubject.next(devices);
        }, audioInputDevices, audioOutputDevices, videoInputDevices);
        meetingSession.audioVideo.addDeviceChangeObserver(this.deviceManager);
    }
    async createMediaStream(config) {
        this.initializeSession();
        invariant(this.meetingSession, 'Meeting session should be defined');
        // If this is a host session we should always create a local video track. If it is a spectator session we only want to
        //       create a local track if the remote client can receive video.
        const shouldCreateLocalVideoTrack = !!config.constraints?.video && (this.isHostSession || this.canReceiveVideo);
        this.meetingSession.audioVideo.setDeviceLabelTrigger(() => {
            return navigator.mediaDevices.getUserMedia({
                audio: true,
                video: shouldCreateLocalVideoTrack && {
                    width: {
                        ideal: VIDEO_WIDTH,
                    },
                    height: {
                        ideal: VIDEO_HEIGHT,
                    },
                },
            });
        });
        this.chimeConnectionObserver = new ChimeConnectionObserver(this.onReconnecting);
        this.meetingSession.audioVideo.addObserver(this.chimeConnectionObserver);
        this.chimeMeetingObserver = new ChimeMeetingObserver(this.onReconnected);
        invariant(this.meetingSession.eventController, 'Event controller should be defined');
        this.meetingSession.eventController.addObserver(this.chimeMeetingObserver);
        this.lowBandwidthManager = new LowBandwidthManager({
            audioVideo: this.meetingSession.audioVideo,
            attendeeId: this.roomVideoCredentials.Attendee.AttendeeId,
            externalUserIdForLocalUser: this.roomVideoCredentials.Attendee.ExternalUserId,
            localVideoTrackEnabledObservable: this.isLocalVideoTrackEnabled,
            remoteVideoTrackEnabledObservable: this.isRemoteVideoTrackEnabled,
            chimeConnectionObserver: this.chimeConnectionObserver,
            lowBandwidthOptimizationEnabled: this.lowBandwidthOptimizationEnabled,
        });
        this.videoPriorityBasedPolicyManager = new VideoPriorityBasedPolicyManager(this.meetingSession.audioVideo, this.videoPriorityBasedPolicy);
        this.remoteAttendeeManager = new RemoteAttendeeManager(this.roomVideoCredentials.Attendee.AttendeeId, this.meetingSession.audioVideo, this.onRemoteAudioLevelUpdate, this.onRemoteSignalStrengthUpdate, this.onRemoteAttendeeAdded, this.onRemoteAttendeeRemoved, this.videoPriorityBasedPolicyManager);
        this.remoteAttendeeObserver = new RemoteAttendeeObserver(this.meetingSession.audioVideo, this.remoteAttendeeManager, this.onRemoteVideoAvailableChange);
        this.meetingSession.audioVideo.addObserver(this.remoteAttendeeObserver);
        try {
            await this.registerDeviceManager(this.meetingSession);
        }
        catch (e) {
            this.streemLogger.error('Failed to register device change manager', {
                errorMessage: e.message,
                stack: e.stack,
            });
        }
        invariant(this.deviceManager, 'Device manager should be defined');
        this.deviceManager.getUserMediaVideoError.subscribe(videoPermissionError => {
            this.getUserMediaVideoErrorSubject.next(videoPermissionError);
        });
        this.deviceManager.getUserMediaAudioError.subscribe(audioPermissionError => {
            this.getUserMediaAudioErrorSubject.next(audioPermissionError);
        });
        // Create Audio Track
        let localAudioTrackCreated = false;
        try {
            const localAudioTrack = new ChimeLocalAudioTrack(this.meetingSession.audioVideo, this.roomVideoCredentials.Attendee.AttendeeId, this.roomVideoCredentials.Attendee.ExternalUserId, this.localAudioLevelSubject);
            localAudioTrackCreated = await localAudioTrack.initializeAudioTrack(config.constraints?.audio, this.deviceManager.audioInputDevices);
            this.localAudioTrackSubject.next(localAudioTrack);
            this.localAttendeeManagedTracks.push(localAudioTrack);
        }
        catch (error) {
            // This error is thrown when the user denies permission to use the microphone
            // We get "OverconstrainedError" when we fail to startAudioInput() in initializeAudioTrack
            if (error.name === 'OverconstrainedError') {
                this.getUserMediaAudioErrorSubject.next('NotAllowedError');
            }
            else {
                this.getUserMediaAudioErrorSubject.next(error.name);
            }
            this.streemLogger.error(`Failed to initialize local audio track:`, {
                errorMessage: error.message,
                stack: error.stack,
            });
        }
        let localVideoTrackCreated = false;
        let chimeLocalVideoTrack = undefined;
        if (shouldCreateLocalVideoTrack) {
            try {
                chimeLocalVideoTrack = new ChimeLocalVideoTrack(this.meetingSession.audioVideo, this.roomVideoCredentials.Attendee.ExternalUserId, this.preferredVideoCodecs);
                await chimeLocalVideoTrack.initializeVideoTrack(config.constraints?.video, this.deviceManager.videoInputDevices);
                this.localVideoTrackSubject.next(chimeLocalVideoTrack);
                this.localAttendeeManagedTracks.push(chimeLocalVideoTrack);
                localVideoTrackCreated = true;
            }
            catch (error) {
                // This error is thrown when the user denies permission to use the camera
                // We get "PermissionDeniedError" when we fail to startVideoInput() in initializeVideoTrack
                if (error.name === 'PermissionDeniedError') {
                    this.getUserMediaVideoErrorSubject.next('NotAllowedError');
                }
                else {
                    this.getUserMediaVideoErrorSubject.next(error.name);
                }
                this.streemLogger.error('Failed to initialize local video track', {
                    errorMessage: error.message,
                    stack: error.stack,
                });
            }
        }
        let localDataTrackCreated = false;
        try {
            const localDataTrack = new ChimeLocalDataTrack(this.meetingSession.audioVideo);
            this.localDataTrackSubject.next(localDataTrack);
            this.localAttendeeManagedTracks.push(localDataTrack);
            localDataTrackCreated = true;
        }
        catch (error) {
            this.streemLogger.error('Failed to initialize local data track', {
                errorMessage: error.message,
                stack: error.stack,
            });
        }
        // create the volumeIndicatorCallback for the local audio track
        this.localAudioVolumeIndicatorCallback = createVolumeIndicatorCallback(this.roomVideoCredentials.Attendee.AttendeeId, this.onLocalAudioLevelUpdate);
        this.meetingSession.audioVideo.realtimeSubscribeToVolumeIndicator(this.roomVideoCredentials.Attendee.AttendeeId, this.localAudioVolumeIndicatorCallback);
        // There is only one remote audio track for the meeting, so we do not need to wait for a remote
        //   attendee to join the room
        let remoteAudioTrackCreated = false;
        try {
            const remoteAudioTrack = new ChimeRemoteAudioTrack(this.meetingSession.audioVideo, this.roomVideoCredentials.Attendee.AttendeeId, this.roomVideoCredentials.Attendee.ExternalUserId, this.remoteAudioLevelSubject);
            await remoteAudioTrack.initializeAudioTrack(this.deviceManager.audioOutputDevices, config.preferredAudioOutputDeviceId);
            this.remoteAudioTrackSubject.next(remoteAudioTrack);
            // associate the remote audio track with the local attendeeId
            this.localAttendeeManagedTracks.push(remoteAudioTrack);
            remoteAudioTrackCreated = true;
        }
        catch (error) {
            this.streemLogger.error('Failed to initialize remote audio track', {
                errorMessage: error.message,
                stack: error.stack,
            });
        }
        this.meetingSession.audioVideo.start();
        // Enables camera on in lobby for CV.
        // Enables camera on in EV2. This was previously handled in the EV2 app.
        if (chimeLocalVideoTrack) {
            chimeLocalVideoTrack.setEnabled(config.video?.active ?? false);
        }
        return {
            audio: localAudioTrackCreated && remoteAudioTrackCreated,
            video: localVideoTrackCreated,
            data: localDataTrackCreated,
        };
    }
    async connect(roomId, _) {
        // should be tested in all 4 browsers!
        const permissionState = await getPermissionsState();
        this.streemLogger.setContextValue(contextValues.PERMISSIONS_STATE, permissionState);
        invariant(this.chimeMeetingObserver, 'ChimeMeetingObserver should be defined');
        await this.chimeMeetingObserver.waitForMeetingStart();
        this.connectionStateSubject.next('CONNECTED');
        this.streemLogger.info('Connected to room: ', roomId.toString());
        invariant(this.chimeConnectionObserver, 'ChimeConnectionObserver should be defined');
        this.roomMonitor = new RoomMonitor(this.roomVideoCredentials.Attendee.AttendeeId, this.connectionState, this.isLocalAudioTrackEnabled, this.isLocalVideoTrackEnabled, this.chimeConnectionObserver);
        this.roomWebRtcStatsAndNetworkQualitySubscription = interval(5000)
            .pipe(withLatestFrom(this.roomMonitor.roomWebRtcStats))
            .subscribe(([_, webRtcStats]) => {
            this.logStats(webRtcStats);
        });
        this.networkQualityObserver = new NetworkQualityObserver({
            roomWebRtcStats: this.roomMonitor.roomWebRtcStats,
            isLocalVideoTrackEnabled: this.isLocalVideoTrackEnabled,
            isLocalAudioTrackEnabled: this.isLocalAudioTrackEnabled,
            isRemoteVideoTrackEnabled: this.isRemoteVideoTrackEnabled,
            isRemoteVideoTrackBackgrounded: this.isRemoteVideoTrackBackgrounded,
            remoteSignalStrength: this.remoteSignalStrength,
            onLocalNetworkQualityChange: this.onLocalNetworkQualityChange,
            onRemoteNetworkQualityChange: this.onRemoteNetworkQualityChange,
        });
    }
    async disconnect() {
        // disconnect cleans up all resources (like a destructor), not only resources created by connect()
        this.remoteCapabilitiesListener?.unsubscribe();
        this.roomWebRtcStatsAndNetworkQualitySubscription?.unsubscribe();
        invariant(this.meetingSession, 'Meeting session should be defined');
        await Promise.allSettled(this.localAttendeeManagedTracks.map(track => track.disconnect()));
        this.meetingSession?.audioVideo.realtimeUnsubscribeFromVolumeIndicator(this.roomVideoCredentials.Attendee.AttendeeId, this.localAudioVolumeIndicatorCallback);
        this.remoteAttendeeObserver?.disconnect();
        this.remoteAttendeeManager?.disconnect();
        // @ts-expect-error TS2345: this.remoteAttendeeObserver should be defined here
        this.meetingSession.audioVideo.removeObserver(this.remoteAttendeeObserver);
        this.networkQualityObserver?.disconnect();
        this.roomMonitor?.disconnect();
        // It is expected that these properties are defined when disconnect is called, so ignoring the
        //     TS errors rather than adding checks for each one
        // @ts-expect-error TS2345: this.deviceManager should be defined here
        this.meetingSession.audioVideo.removeDeviceChangeObserver(this.deviceManager);
        // @ts-expect-error TS2345: this.chimeConnectionObserver should be defined here
        this.meetingSession.audioVideo.removeObserver(this.chimeConnectionObserver);
        this.meetingSession.audioVideo.stop();
        await this.chimeMeetingObserver?.waitForMeetingStop();
        this.connectionStateSubject.next('DISCONNECTED');
        // @ts-expect-error TS2345: this.chimeMeetingObserver should be defined here
        this.meetingSession.eventController?.removeObserver(this.chimeMeetingObserver);
        // @ts-expect-error TS2345: this.videoDownlinkObserver should be defined here
        this.videoPriorityBasedPolicy?.removeObserver(this.videoDownlinkObserver);
        this.lowBandwidthManager?.disconnect();
        await this.deviceController.destroy();
        await this.meetingSession?.destroy();
        this.resetState();
        this.streemLogger.removeContextValue(contextValues.PERMISSIONS_STATE);
        this.streemLogger.removeContextValue(contextValues.ROOM_ID);
        this.streemLogger.removeContextValue(contextValues.CHIME_ROOM_ID);
        this.streemLogger.info(`The room has been disconnected, roomId=${this.streemRoomId?.toString()}`);
    }
    resetState() {
        this.remoteConnectionSubject.next(false);
        this.localAudioTrackSubject.next(undefined);
        this.localVideoTrackSubject.next(undefined);
        this.localDataTrackSubject.next(undefined);
        this.remoteAudioTrackSubject.next(undefined);
        this.remoteVideoTrackSubject.next(undefined);
        this.remoteDataTrackSubject.next(undefined);
        this.localNetworkQualitySubject.next(0);
        this.remoteConnectionSubject.next(false);
        this.getUserMediaVideoErrorSubject.next(undefined);
        this.getUserMediaAudioErrorSubject.next(undefined);
        this.localAttendeeManagedTracks = [];
        this.meetingSession = undefined;
        this.deviceManager = undefined;
        this.chimeConnectionObserver = undefined;
        this.chimeMeetingObserver = undefined;
        this.lowBandwidthManager = undefined;
        this.remoteAttendeeManager = undefined;
        this.remoteAttendeeObserver = undefined;
        this.roomMonitor = undefined;
        this.networkQualityObserver = undefined;
        this.videoPriorityBasedPolicy = null;
        this.videoDownlinkObserver = undefined;
        this.videoPriorityBasedPolicyManager = undefined;
    }
    buildIsVideoTrackEnabledObservable(videoTrack) {
        return videoTrack.pipe(switchMap(track => (track ? track.enabled : of(false))), shareReplay(1));
    }
    buildIsAudioTrackEnabledObservable(audioTrack) {
        return audioTrack.pipe(switchMap(track => (track ? track.enabled : of(false))), shareReplay(1));
    }
    buildIsVideoTrackBackgroundedObservable(videoTrack) {
        return videoTrack.pipe(switchMap(track => (track && track.backgrounded ? track.backgrounded : of(false))), shareReplay(1));
    }
    async logStats(statsReport) {
        const { localVideoTrackStats, localAudioTrackStats, remoteVideoTrackStats, remoteAudioTrackStats, } = statsReport;
        const compositeMediaStats = {
            localVideo: localVideoTrackStats[0]
                ? {
                    ...localVideoTrackStats[0],
                }
                : {},
            localAudio: localAudioTrackStats[0]
                ? {
                    ...localAudioTrackStats[0],
                }
                : {},
            remoteVideo: remoteVideoTrackStats[0]
                ? {
                    ...remoteVideoTrackStats[0],
                }
                : {},
            remoteAudio: remoteAudioTrackStats[0]
                ? {
                    ...remoteAudioTrackStats[0],
                }
                : {},
        };
        this.streemLogger.setContextValue(contextValues.CHIME_WEB_RTC_STATS, compositeMediaStats);
        this.streemLogger.info('Chime WebRTC stats');
        this.streemLogger.removeContextValue(contextValues.CHIME_WEB_RTC_STATS);
    }
}
//# sourceMappingURL=chime_room.js.map